/* istanbul ignore file */
import { makeFragmentData } from '@packages/gql/generated/shopping';
import { DownloadItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/DownloadItemFragmentFragmentDoc';
import type { EfficiencyFlagProps } from '@packages/shared';
import {
  Box,
  EfficiencyFlag,
  Flag,
  Image,
  Typography,
  Stack,
  ProductPrice,
} from '@packages/shared';
import { useDeviceType } from '@packages/shared/src/hooks/useDeviceType/useDeviceType';
import { Fragment } from 'react';
import { useIntl } from 'react-intl';
import type {
  AvailabilityState,
  LineItemFragmentFragment,
  SavingType,
} from '@packages/gql/generated/shopping/graphql';
import type { PaletteColor } from '@mui/material';
import { DownloadItem } from '@packages/modules/src/DownloadItem/DownloadItem';
import { ServiceItems } from '../ServiceItems';
import { LegacyEnergyFlag } from './LegacyEnergyFlag';
import { usePriceFormater } from '../../../hooks/usePriceFormater';
import type { TaxHintProps } from '../../TaxHint';
import { TaxHint } from '../../TaxHint';

// TODO: check for mapper
const mapTireEfficiency = (text?: string): EfficiencyFlagProps['type'] => {
  switch (text) {
    case 'Nasshaftung':
      return 'wetGrip';
    case 'Rollgeraeusch':
      return 'externalRollingNoise';
    default:
      return 'fuelEfficiency';
  }
};

type AddedProductProps = {
  lineItem: LineItemFragmentFragment;
  taxHintData: TaxHintProps['data'];
};

const availabilityStateColors: Record<AvailabilityState, PaletteColor['main']> = {
  AVAILABLE: 'success.main',
  NOT_AVAILABLE: 'error.main',
  NOT_YET_AVAILABLE: 'warning.main',
  UNSELECTED: 'primary.main',
};

export const AddedProduct = ({ lineItem, taxHintData }: AddedProductProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('ATB');
  const { isMobile } = useDeviceType();

  const { product } = lineItem;

  if (!product) {
    return null;
  }
  const {
    imageUrl,
    name,
    variation,
    powerEfficiencyFlags: powerEfficiency,
    marketingFlag,
  } = product;

  const {
    id,
    price,
    quantity,
    singlePrice,
    aplus,
    availability: { message: deliveryMessage, state: availabilityState },
  } = lineItem;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box
          sx={{ flexShrink: 0, position: 'relative', width: [80, 80, 120], height: [80, 80, 120] }}
        >
          <Image src={imageUrl} style={{ objectFit: 'contain' }} fill alt="" sizes="25vw" />
        </Box>
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {marketingFlag && (
            <div>
              <Flag text={marketingFlag.text} type="contrast" />
            </div>
          )}
          <Typography
            sx={{
              typography: ['h6', 'h6', 'h5'],
              fontWeight: ['fontWeightSemiBold', 'fontWeightSemiBold', 'normal'],
            }}
          >
            {name}
          </Typography>
          <Typography variant="body2">{variation}</Typography>
          <Box>
            <Typography variant="body2">
              {singlePrice &&
                formatMessage(
                  {
                    id: 'atblayer.singleprice.label',
                    defaultMessage: 'Anzahl {quantity} (á {singlePrice})',
                  },
                  {
                    quantity,
                    singlePrice: formatPrice(singlePrice.amount, singlePrice.currency),
                  },
                )}
            </Typography>
            <Typography variant="body2" sx={{ color: availabilityStateColors[availabilityState] }}>
              {deliveryMessage}
            </Typography>
          </Box>
          <Box
            sx={{ display: 'flex', flexDirection: ['column', 'column', 'row'], gap: 1, mt: 1.5 }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {powerEfficiency && powerEfficiency.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {powerEfficiency.map((p, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Fragment key={i}>
                        {p.iconText ? (
                          <EfficiencyFlag
                            text={p.iconText}
                            overlay={{ title: p.text || '', imgUrl: p.link }}
                            type={mapTireEfficiency(p.text)}
                          />
                        ) : (
                          <LegacyEnergyFlag
                            icon={`${p.icon}.svg`}
                            overlayImg={p.link}
                            overlayTitle={p.text}
                          />
                        )}
                      </Fragment>
                    ))}
                  </Box>
                  <Stack spacing={1} mt={1}>
                    {powerEfficiency.map(
                      (item, idx) =>
                        item.dataSheet && (
                          <DownloadItem
                            data={makeFragmentData(
                              { link: item.dataSheet, type: 'PRODUCT_DATASHEET' },
                              DownloadItemFragmentFragmentDoc,
                            )}
                            // index should only be shown, if there is more then one download
                            idx={powerEfficiency.length > 1 ? idx + 1 : undefined}
                            renderIcon={false}
                            // eslint-disable-next-line react/no-array-index-key
                            key={idx}
                          />
                        ),
                    )}
                  </Stack>
                </>
              )}
            </Box>
            <Stack>
              <ProductPrice
                fontVariantPrice={isMobile ? 'h6' : 'h5'}
                currency={price.currency}
                isStartPrice={price.isStartPrice || undefined}
                oldPrice={price.valueOld || undefined}
                unit={price.unit || undefined}
                unitPrice={price.valueUnit || undefined}
                price={price.value}
                discountType={price.saving?.type?.toLowerCase() as Lowercase<SavingType>}
                saving={price.saving?.value || undefined}
                hasDiscountFlag={!price.discount}
                alignment="right"
                uvpLabel={price.uvp ? 'UVP' : undefined}
                displayNegativeDiscountNumber
              />
              <TaxHint data={taxHintData} />
            </Stack>
          </Box>
        </Box>
      </Box>
      {aplus && id && <ServiceItems aplusData={aplus} pli={id} />}
    </Box>
  );
};
